import { getSpreadHeightFromSpread } from 'editor/src/store/design/selector/getSpreadHeight';
import getSpreadWidthFromSpread from 'editor/src/store/design/selector/getSpreadWidthFromSpread';
import { DesignData, PrintTechnology } from 'editor/src/store/design/types';
import { GalleryImage } from 'editor/src/store/gallery/types';

import analyzeImageDataStats from './analyzeImageDataStats';
import { deriveSignals, emptyPixelStats, mergePixelStats } from './deriveSignals';
import hashDesignForAnalysis from './hashDesignForAnalysis';
import loadRasterFromUrl from './loadRasterFromUrl';
import { PrintTechAnalysis, PrintTechAreaAnalysis, PRINT_TECH_ANALYSIS_VERSION, RasterPixels } from './types';

type RasterLoader = (url: string) => Promise<RasterPixels | null>;

// Only DTG/DTF spreads are analyzed — embroidery is out of scope for the suggestion.
const ANALYZED_TECHNOLOGIES = new Set<PrintTechnology>([PrintTechnology.DTG, PrintTechnology.DTF]);

function resolveImageUrl(
  element: { type: string; imageId?: string; url?: string },
  images: GalleryImage[],
): string | undefined {
  if (element.url) {
    return element.url;
  }
  if (element.imageId) {
    return images.find((image) => image.id === element.imageId)?.url;
  }
  return undefined;
}

/**
 * GAPI-43148 — walks the design, rasterizes each DTG/DTF image element and
 * aggregates artwork signals per print area. Returns raw signals only; the host
 * decides DTF vs DTG. The `loadRaster` seam keeps this testable.
 */
async function collectPrintTechAnalysis(
  designData: DesignData | undefined,
  images: GalleryImage[],
  loadRaster: RasterLoader = loadRasterFromUrl,
): Promise<PrintTechAnalysis> {
  const areas: PrintTechAreaAnalysis[] = [];
  const designHash = hashDesignForAnalysis(designData);

  if (!designData?.spreads?.length) {
    return { analysisVersion: PRINT_TECH_ANALYSIS_VERSION, designHash, areas };
  }

  for (let spreadIndex = 0; spreadIndex < designData.spreads.length; spreadIndex += 1) {
    const spread = designData.spreads[spreadIndex];

    if (!spread.technology || !ANALYZED_TECHNOLOGIES.has(spread.technology)) {
      continue;
    }

    const spreadWidth = getSpreadWidthFromSpread(spread);
    const spreadHeight = getSpreadHeightFromSpread(spread);
    const spreadArea = spreadWidth * spreadHeight;
    const mediaElements = spread.pages?.[0]?.groups?.media || [];

    const aggregated = emptyPixelStats();
    let coverageArea = 0;

    for (let elementIndex = 0; elementIndex < mediaElements.length; elementIndex += 1) {
      const element = mediaElements[elementIndex];
      if (element.hidden || (element.type !== 'image' && element.type !== 'addon')) {
        continue;
      }

      const url = resolveImageUrl(element, images);
      if (!url) {
        continue;
      }

      // eslint-disable-next-line no-await-in-loop
      const raster = await loadRaster(url);
      if (!raster) {
        continue;
      }

      const stats = analyzeImageDataStats(raster);
      mergePixelStats(aggregated, stats);

      // Coverage: weight each element's opaque fraction by its area on the print area.
      const opaqueFraction = stats.sampledPixels ? stats.strongOpaquePixels / stats.sampledPixels : 0;
      const elementArea = Math.max(0, element.width) * Math.max(0, element.height);
      coverageArea += opaqueFraction * elementArea;
    }

    if (aggregated.sampledPixels === 0) {
      continue;
    }

    const coverage = spreadArea > 0 ? coverageArea / spreadArea : 0;
    areas.push({
      spreadIndex,
      spreadName: spread.name,
      technology: spread.technology,
      signals: deriveSignals(aggregated, coverage),
    });
  }

  return { analysisVersion: PRINT_TECH_ANALYSIS_VERSION, designHash, areas };
}

export default collectPrintTechAnalysis;
